import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Alert } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { ClassCard } from "@/components/ClassCard";
import { CreateScheduleModal } from "@/components/CreateScheduleModal";
import { ScheduleManageModal } from "@/components/ScheduleManageModal";
import { EditScheduleModal } from "@/components/EditScheduleModal";
import { QueryStateHandler } from "@/components/QueryStateHandler";
import { useToast } from "@/components/ToastProvider";
import { isDemoMode } from "@/demo/demoMode";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useDate } from "@/hooks/useDate";
import { useStudiomateSchedules } from "@/hooks/useStudiomateSchedules";
import { getClassCompletionsBatch } from "@/services/firestore/classCompletions";
import { getBranchPolicy } from "@/services/firestore/branchPolicy";
import { fetchStudiomateCounsels } from "@/services/studiomate-counsel";
import { detectTrialCounsel } from "@vlab-corporation/vlab-pilates-default-service/class";
import { sameStaffId, resolveMyStaffKey } from "@/utils/staff-identity";
import type { ClassWithCompletion } from "@/hooks/useClasses";
import type { StudiomateScheduleItem } from "@vlab-corporation/studiomate-api";
import type { AuthUser } from "@/types/auth";

export function ClassListPage() {
  const user = useAuthenticatedUser();
  if (!user) return null;
  return <ClassListPageContent user={user} />;
}

function ClassListPageContent({ user }: { user: AuthUser }) {
  const { date, goToPrev, goToNext, goToToday } = useDate();
  const toast = useToast();
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [manageTarget, setManageTarget] = useState<StudiomateScheduleItem | null>(null);
  const [editTarget, setEditTarget] = useState<StudiomateScheduleItem | null>(null);
  const { centerOwnerUid, centerId, centerName, instructorId } = user.activeMembership;
  const myStaffKey = resolveMyStaffKey(user.activeMembership);

  const { data: branchPolicy } = useQuery({
    queryKey: ["branchPolicy", centerId],
    queryFn: () => getBranchPolicy(centerOwnerUid, centerId),
    staleTime: 5 * 60 * 1000,
  });
  const journalWritingStartDate = branchPolicy?.journalWritingStartDate;
  const isBeforeStartDate = !!journalWritingStartDate && date < journalWritingStartDate;

  const { data, isLoading, error, refetch } = useStudiomateSchedules({ date }, centerId);

  // 체험단 상담 조회
  const { data: counselData } = useQuery({
    queryKey: ["studiomate-counsels", centerId, date],
    queryFn: () => fetchStudiomateCounsels({ startDate: date, endDate: date }),
    staleTime: 5 * 60 * 1000,
  });

  const mySchedules = useMemo(() => {
    if (!data?.schedules) return [];
    return data.schedules.filter((s) => sameStaffId(s.staffId, myStaffKey));
  }, [data, myStaffKey]);

  const classIds = useMemo(() => {
    return mySchedules.map((s) => String(s.id));
  }, [mySchedules]);

  const { data: completions } = useQuery({
    queryKey: ["classCompletions-batch", centerId, date, classIds],
    queryFn: () => getClassCompletionsBatch(centerOwnerUid, centerId, classIds),
    enabled: classIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const classes = useMemo<ClassWithCompletion[]>(() => {
    const lectureClasses: ClassWithCompletion[] = mySchedules.map((s) => ({
      id: String(s.id),
      type: "studiomate" as const,
      date: s.date,
      status: "scheduled",
      instructorId,
      journalWritten: completions?.get(String(s.id))?.journalWritten ?? false,
      title: s.title,
      startTime: s.startTime,
      endTime: s.endTime,
      staffName: s.staffName,
      currentTrainees: s.currentTrainees,
      maxTrainees: s.maxTrainees,
      lectureType: s.type,
      bookings: s.bookings,
    }));

    // 체험단 상담 → ClassWithCompletion 변환
    const counselClasses: ClassWithCompletion[] = (counselData?.counsels ?? [])
      .filter(
        (c) => detectTrialCounsel(c.name) && c.date === date && sameStaffId(c.staffId, myStaffKey),
      )
      .map((c) => ({
        id: `counsel_${c.id}`,
        type: "counsel" as const,
        date: c.date,
        status: "completed",
        instructorId,
        journalWritten: true, // 체험단은 자동 완료
        title: c.name,
        startTime: c.startTime,
        endTime: c.endTime,
        staffName: c.staffName,
      }));

    // 시간순 병합
    return [...lectureClasses, ...counselClasses].sort((a, b) =>
      (a.startTime ?? "").localeCompare(b.startTime ?? ""),
    );
  }, [mySchedules, instructorId, completions, counselData, date, myStaffKey]);

  const apiError = data?.error;

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={goToPrev}>
          ←
        </Button>
        <div className="text-center">
          <h2 className="text-lg font-semibold">{date}</h2>
          <button onClick={goToToday} className="text-xs text-primary-500 hover:text-primary-600">
            오늘
          </button>
        </div>
        <Button variant="ghost" onClick={goToNext}>
          →
        </Button>
      </div>

      {!isDemoMode() && (
        <div className="flex justify-end mb-4">
          <Button variant="primary" size="sm" onClick={() => setIsScheduleOpen(true)}>
            수업 예약
          </Button>
        </div>
      )}

      {apiError && <Alert variant="warning">{apiError.message}</Alert>}

      {isBeforeStartDate && (
        <Alert variant="info" className="mb-3">
          수업 일지 작성 시작일({journalWritingStartDate}) 이전의 수업은 일지 작성이 불가합니다.
        </Alert>
      )}

      <QueryStateHandler
        isLoading={isLoading}
        error={error}
        errorMessage="수업 목록을 불러오지 못했습니다."
        onRetry={() => refetch()}
        isEmpty={classes.length === 0 && !apiError}
        emptyMessage="이 날짜에 수업이 없습니다."
      >
        <div className="space-y-3">
          {classes.map((c) => (
            <ClassCard
              key={c.id}
              classItem={c}
              subdomain={data?.subdomain}
              onManage={
                !isDemoMode() && c.type === "studiomate"
                  ? () => {
                      const sched = mySchedules.find((s) => String(s.id) === c.id);
                      if (sched) setManageTarget(sched);
                    }
                  : undefined
              }
            />
          ))}
        </div>
      </QueryStateHandler>

      <CreateScheduleModal
        isOpen={isScheduleOpen}
        ownerUid={centerOwnerUid}
        centerId={centerId}
        centerName={centerName}
        onClose={() => setIsScheduleOpen(false)}
        onCreated={() => {
          setIsScheduleOpen(false);
          void refetch();
          toast.show("수업이 예약되었습니다.", "success");
        }}
      />

      {/* 수업 관리 (상세·예약취소·삭제) */}
      {manageTarget && (
        <ScheduleManageModal
          isOpen
          centerId={centerId}
          schedule={manageTarget}
          onClose={() => setManageTarget(null)}
          onEdit={() => {
            setEditTarget(manageTarget);
            setManageTarget(null);
          }}
          onChanged={() => void refetch()}
        />
      )}

      {/* 수업 변경 (강사 가능) */}
      {editTarget && (
        <EditScheduleModal
          isOpen
          centerId={centerId}
          schedule={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={() => {
            setEditTarget(null);
            void refetch();
            toast.show("수업이 변경되었습니다.", "success");
          }}
        />
      )}
    </AppLayout>
  );
}
